import {useCallback, useEffect, useState, type SetStateAction} from 'react';

/**
 * Persists a paginated list's search term, filters and page to localStorage per `identifier`.
 * @registry localStorage-backed search/filter/page state for DataList/Paginator lists; list counterpart of useTableState.
 */

export interface UseListStateOptions<F extends object> {
  identifier: string;
  pageSize: number;
  /** Must name every filter key (`null` = no filter): restore keeps only these
   * keys and drops a saved value whose type no longer matches its default. */
  defaultFilters?: F;
  defaultSearchTerm?: string;
}

interface ListState<F> {
  identifier: string;
  searchTerm: string;
  filters: F;
  currentPage: number;
}

const storageKey = (identifier: string) => `${identifier}_list_state`;

const isSameKind = (saved: unknown, fallback: unknown) =>
  fallback === null ||
  fallback === undefined ||
  (saved !== null &&
    Array.isArray(saved) === Array.isArray(fallback) &&
    typeof saved === typeof fallback);

const restoreFilters = <F extends object>(saved: unknown, defaults: F): F => {
  if (!saved || typeof saved !== 'object') return defaults;
  const source = saved as Record<string, unknown>;
  const restored = {...defaults} as Record<string, unknown>;
  Object.keys(defaults).forEach(key => {
    if (key in source && isSameKind(source[key], restored[key])) {
      restored[key] = source[key];
    }
  });
  return restored as F;
};

const initialState = <F extends object>(
  identifier: string,
  defaultFilters: F,
  defaultSearchTerm: string
): ListState<F> => ({
  identifier,
  searchTerm: defaultSearchTerm,
  filters: defaultFilters,
  currentPage: 1,
});

const readState = <F extends object>(
  identifier: string,
  defaultFilters: F,
  defaultSearchTerm: string
): ListState<F> => {
  const fallback = initialState(identifier, defaultFilters, defaultSearchTerm);
  try {
    const raw = localStorage.getItem(storageKey(identifier));
    if (!raw) return fallback;
    const saved = JSON.parse(raw) as Record<string, unknown>;
    const page = saved.currentPage;
    return {
      identifier,
      searchTerm:
        typeof saved.searchTerm === 'string'
          ? saved.searchTerm
          : fallback.searchTerm,
      filters: restoreFilters(saved.filters, defaultFilters),
      currentPage:
        typeof page === 'number' && Number.isInteger(page) && page > 0
          ? page
          : 1,
    };
  } catch {
    return fallback;
  }
};

export const useListState = <F extends object = Record<string, never>>({
  identifier,
  pageSize,
  defaultFilters = {} as F,
  defaultSearchTerm = '',
}: UseListStateOptions<F>) => {
  const [stored, setState] = useState(() =>
    readState(identifier, defaultFilters, defaultSearchTerm)
  );

  // A reused instance whose identifier changes (e.g. a route param) switches to that key's state.
  let state = stored;
  if (stored.identifier !== identifier) {
    state = readState(identifier, defaultFilters, defaultSearchTerm);
    setState(state);
  }

  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey(state.identifier),
        JSON.stringify({
          searchTerm: state.searchTerm,
          filters: state.filters,
          currentPage: state.currentPage,
        })
      );
    } catch {
      // Storage full or blocked: the list still works, it just won't restore.
    }
  }, [state]);

  const setSearchTerm = useCallback(
    (searchTerm: string) =>
      setState(prev =>
        prev.searchTerm === searchTerm
          ? prev
          : {...prev, searchTerm, currentPage: 1}
      ),
    []
  );

  const setFilters = useCallback(
    (update: SetStateAction<F>) =>
      setState(prev => ({
        ...prev,
        filters:
          typeof update === 'function'
            ? (update as (prev: F) => F)(prev.filters)
            : update,
        currentPage: 1,
      })),
    []
  );

  const setCurrentPage = useCallback(
    (currentPage: number) => setState(prev => ({...prev, currentPage})),
    []
  );

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => setCurrentPage(page),
    [setCurrentPage]
  );

  const resetState = () =>
    setState(initialState(identifier, defaultFilters, defaultSearchTerm));

  return {
    searchTerm: state.searchTerm,
    setSearchTerm,
    filters: state.filters,
    setFilters,
    currentPage: state.currentPage,
    setCurrentPage,
    handlePageChange,
    start: (state.currentPage - 1) * pageSize,
    length: pageSize,
    resetState,
  };
};
